import React from 'react';
import UITree from 'react-ui-tree';
import store from '../../store';
import tags from 'app/core/utils/tags';
import cx from 'classnames';

export interface IProps {
  tree: any;
  searchterm: any;
  searchinprogress: any;
  searcherror: any;
  dragStart: any;
  dragEnd: any;
  handleChange: any;
  onEdit: any;
  onView: any;
  onAdd: any;
  matlabPluginUrlClick: any;
}

export default class Tree extends React.Component<IProps, any> {
  constructor(props) {
    super(props);
    this.state = {};

    if (props.tree) {
      this.state = this.initTreeState(props.tree);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tree !== this.props.tree) {
      this.setState(this.initTreeState(this.props.tree));
    }
  }

  initTreeState(tree) {
    let jsonString = store.get('experimentsTreeCollapsed');
    let collapsedMap = jsonString ? JSON.parse(jsonString) : {};

    this.initCollapsedStates(tree, collapsedMap);
    return {
      collapsedMap: collapsedMap,
      tree: tree,
    };
  }

  initCollapsedStates(tree, collapsedMap) {
    let states = Object.assign({}, collapsedMap);
    this.initCollapsedStatesHelper(tree, states);
    for (let key of Object.keys(states)) {
      // remove states for objects that have been deleted
      delete collapsedMap[key];
    }
    store.set('experimentsTreeCollapsed', JSON.stringify(collapsedMap));
  }

  initCollapsedStatesHelper(tree, states) {
    if (tree.type === 'sensor') {
      return;
    }

    if (tree.id === 0) {
      tree.collapsed = false;
    } else {
      let collapsed = states[tree.id];
      if (typeof collapsed === 'undefined') {
        collapsed = true;
      }
      tree.collapsed = collapsed;
      delete states[tree.id];
    }

    if (tree.children) {
      tree.children.forEach(c => this.initCollapsedStatesHelper(c, states));
    }
  }

  onCollapse = node => {
    this.state.collapsedMap[node.id] = node.collapsed;
    store.set('experimentsTreeCollapsed', JSON.stringify(this.state.collapsedMap));
  };

  onDragStart = (node, parent) => {
    return e => {
      let dragData;

      if (node.type === 'sensor') {
        dragData = {
          sensor: {
            shortName: node.shortName,
            description: node.description,
            databaseId: node.databaseId,
          },
          experiment: { title: parent.module, id: parent.id },
        };
      } else if (node.type === 'experimentdetails') {
        dragData = {
          meta: true,
          experiment: { title: parent.module, id: parent.id },
        };
      } else if (node.type === 'video') {
        dragData = {
          video: true,
          experiment: { title: parent.module, id: parent.id },
        };
      }

      var offset = { x: e.offsetX, y: e.offsetY };
      var transferDataObject = { data: dragData, channel: 'sensor', offset: offset };
      var transferDataText = JSON.stringify(transferDataObject);

      e.dataTransfer.setData('text', transferDataText);
      e.dataTransfer.effectAllowed = 'copyMove';

      this.props.dragStart(e, 'sensor', transferDataObject);
    };
  };

  onDragEnd = e => {
    this.props.dragEnd(e, 'sensor');
  };

  renderNode = (node, parent, indexId) => {
    return (
      <span
        key="label"
        className={cx('node', node.type)}
        draggable={node.leaf}
        onDragStart={this.onDragStart(node, parent)}
        onDragEnd={this.onDragEnd}
      >
        {node.type === 'experiment' ? <span className="icon fa fa-flask" /> : ''}

        {node.module}
      </span>
    );
  };

  renderTags = node => {
    // The 1px-wide span forces the first tag to wrap if there is not enough space
    let elements = [<span key="blank" style={{ width: '1px', height: '20px' }} />];

    if (node.tags) {
      node.tags.forEach(tag => {
        const { color, borderColor } = tags.getTagColorsFromName(tag);
        let style = { backgroundColor: color, borderColor: borderColor };
        elements.push(
          <span key={tag} style={style} className="label label-tag">
            {tag}
          </span>
        );
      });
    }

    return <span className="tag-container">{elements}</span>;
  };

  getIcon = node => {
    if (node.type === 'folder') {
      return { open: 'fa fa-folder-open', closed: 'fa fa-folder' };
    }
    if (node.type === 'experiment') {
      return { open: 'fa fa-minus-square', closed: 'fa fa-plus-square' };
    }
    if (node.type === 'experimentdetails') {
      return { open: 'fa fa-file-text-o', closed: 'fa fa-file-text-o' };
    }
    if (node.type === 'video') {
      return { open: 'fa fa-video-camera', closed: 'fa fa-video-camera' };
    }

    return { open: 'fa fa-thermometer-empty', closed: 'fa fa-thermometer-empty' };
  };

  canDrop = (node, parent, dropTarget) => {
    if (node.type === 'sensor') {
      // Sensors have to stay within the experiment
      return parent.id === dropTarget.id;
    }

    // Can't move above Active Experiment entry
    if (dropTarget.type === 'folder' && dropTarget.id === 0) {
      let childIndex = dropTarget.children.indexOf(node);
      return childIndex > 0;
    }

    return dropTarget.type === 'folder';
  };

  canDropInto = (node, dropTarget) => {
    return (node.type === 'experiment' || node.type === 'folder') && dropTarget.type === 'folder';
  };

  render() {
    if (!this.state.tree) {
      return null;
    }

    let experiments = this.state.tree.children.filter(e => e.module !== 'Active experiment');

    if (experiments.length === 0) {
      if (this.props.searchterm != null || this.props.searchterm !== "") {

        if (this.props.searchinprogress) {
          return (
            <div className="tree-notice"><br />Searching for experiments...</div>
          );
        } else if (this.props.searcherror) {
          return (
            <div className="tree-notice"><br />Query Error: { this.props.searcherror }.</div>
          );
        } else {
          return (
            <div className="tree-notice"><br />No experiments found.</div>
          );
        }
      }

      return (
        <div className="tree-notice">
          <span style={{ fontSize: '3em' }} className="fa fa-flask" />
          <br />
          <br />
          Your experiments will show up here.<br />
          Choose an option on the right to get started.
          <br />
        </div>
      );
    }

    return (
      <UITree
        paddingLeft={20}
        tree={this.state.tree}
        onChange={this.props.handleChange}
        onCollapse={this.onCollapse}
        onEdit={this.props.onEdit}
        onAdd={this.props.onAdd}
        onView={this.props.onView}
        renderNode={this.renderNode}
        renderTags={this.renderTags}
        getIcon={this.getIcon}
        canDropInPosition={this.canDrop}
        canDropInto={this.canDropInto}
        searchTerm={this.props.searchterm}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./public/app/core/components/Tree/Tree.tsx